import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, CalendarOff, Save, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const DAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];
const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export const HubSchedule = ({ branch, packages, onSaved }) => {
  const [h, setH] = useState(branch.hub);
  const [allHours, setAllHours] = useState({ closed: false, open: "10:00", close: "22:00" });
  const [newClosure, setNewClosure] = useState({ date: "", note: "" });
  const [newSpecial, setNewSpecial] = useState({ date: "", open: "12:00", close: "20:00", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setH(branch.hub);
    const firstOpenDay = branch.hub?.hours?.find((row) => !row.closed) || branch.hub?.hours?.[0];
    if (firstOpenDay) {
      setAllHours({
        closed: Boolean(firstOpenDay.closed),
        open: firstOpenDay.open,
        close: firstOpenDay.close,
      });
    }
  }, [branch.hub]);
  if (!h) return null;

  const setDay = (i, patch) => setH({ ...h, hours: h.hours.map((r) => (r.day === i ? { ...r, ...patch } : r)) });
  const applyHoursToAll = () => {
    setH({
      ...h,
      hours: h.hours.map((row) => ({ ...row, ...allHours })),
    });
    toast.success("Jam operasional diterapkan ke semua hari");
  };
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/branches/${branch.id}`, {
        hours: h.hours, special_hours: h.special_hours, closures: h.closures,
        lead_time_mins: Number(h.lead_time_mins), latest_pickup: h.latest_pickup,
        dispatch_interval_mins: Number(h.dispatch_interval_mins || 1),
        overnight_enabled: h.overnight_enabled, overnight_min_hours: Number(h.overnight_min_hours),
        overnight_min_package_id: h.overnight_min_package_id || "",
        turnaround_mins: Number(h.turnaround_mins), return_grace_mins: Number(h.return_grace_mins),
        play_at_venue_enabled: h.play_at_venue_enabled,
        partner_commission_percent: Number(h.partner_commission_percent || 0),
      });
      toast.success(`Jam operasional ${branch.name} disimpan`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={`hub-editor-${branch.code}`}>
      <div className="flex items-center gap-2">
        <Clock size={15} className="text-lm-red" />
        <span className="font-semibold">{branch.name}</span>
        <span className="text-xs text-ink-soft">{h.timezone}</span>
        <button data-testid={`hub-save-${branch.code}`} disabled={busy} onClick={save}
          className="ml-auto lm-btn-primary rounded-md px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
          <Save size={13} /> Simpan
        </button>
      </div>

      <div className="mt-4 space-y-1.5">
        {h.hours.map((r) => (
          <div key={r.day} className="flex items-center gap-2 text-sm" data-testid={`hub-day-${branch.code}-${r.day}`}>
            <span className="w-16 text-ink-soft">{DAYS[r.day]}</span>
            <label className="flex items-center gap-1.5 text-xs w-20">
              <input type="checkbox" data-testid={`hub-closed-${branch.code}-${r.day}`} checked={!!r.closed}
                onChange={(e) => setDay(r.day, { closed: e.target.checked })} /> Tutup
            </label>
            <Input type="time" className="h-9 w-28" disabled={r.closed} value={r.open}
              data-testid={`hub-open-${branch.code}-${r.day}`}
              onChange={(e) => setDay(r.day, { open: e.target.value })} />
            <span className="text-ink-soft">–</span>
            <Input type="time" className="h-9 w-28" disabled={r.closed} value={r.close}
              data-testid={`hub-close-${branch.code}-${r.day}`}
              onChange={(e) => setDay(r.day, { close: e.target.value })} />
          </div>
        ))}
      </div>

      <div data-testid={`hub-apply-all-hours-${branch.code}`} className="mt-4 border-y border-black/10 py-4">
        <div className="text-[11px] uppercase tracking-widest text-ink-soft">Terapkan jam ke semua hari</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-1.5 text-xs">
            <input type="checkbox" data-testid={`hub-apply-all-closed-${branch.code}`}
              checked={allHours.closed}
              onChange={(event) => setAllHours({ ...allHours, closed: event.target.checked })} />
            Tutup semua
          </label>
          <Input type="time" className="h-9 w-28" disabled={allHours.closed}
            data-testid={`hub-apply-all-open-${branch.code}`} value={allHours.open}
            onChange={(event) => setAllHours({ ...allHours, open: event.target.value })} />
          <span className="text-ink-soft">–</span>
          <Input type="time" className="h-9 w-28" disabled={allHours.closed}
            data-testid={`hub-apply-all-close-${branch.code}`} value={allHours.close}
            onChange={(event) => setAllHours({ ...allHours, close: event.target.value })} />
          <button data-testid={`hub-apply-all-button-${branch.code}`} onClick={applyHoursToAll}
            className="rounded-md border border-black/15 px-3 py-2 text-xs hover:border-lm-red">
            Terapkan semua
          </button>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Jam mulai terakhir</span>
          <Input type="time" className="mt-1 h-9" data-testid={`hub-latest-pickup-${branch.code}`}
            value={h.latest_pickup} onChange={(e) => setH({ ...h, latest_pickup: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Waktu persiapan (menit)</span>
          <Input type="number" className="mt-1 h-9" data-testid={`hub-lead-${branch.code}`}
            value={h.lead_time_mins} onChange={(e) => setH({ ...h, lead_time_mins: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Interval pickup / dispatch (menit)</span>
          <Input type="number" min="1" max="60" className="mt-1 h-9" data-testid={`hub-dispatch-interval-${branch.code}`}
            value={h.dispatch_interval_mins || 1} onChange={(e) => setH({ ...h, dispatch_interval_mins: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Turnaround / bersih-bersih (menit)</span>
          <Input type="number" className="mt-1 h-9" data-testid={`hub-turnaround-${branch.code}`}
            value={h.turnaround_mins} onChange={(e) => setH({ ...h, turnaround_mins: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Toleransi pengembalian (menit)</span>
          <Input type="number" className="mt-1 h-9" data-testid={`hub-grace-${branch.code}`}
            value={h.return_grace_mins} onChange={(e) => setH({ ...h, return_grace_mins: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Minimum paket menginap (jam)</span>
          <Input type="number" className="mt-1 h-9" data-testid={`hub-overnight-hours-${branch.code}`}
            value={h.overnight_min_hours} onChange={(e) => setH({ ...h, overnight_min_hours: e.target.value })} /></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Paket menginap minimum</span>
          <select className={`${sel} mt-1`} data-testid={`hub-overnight-pkg-${branch.code}`}
            value={h.overnight_min_package_id || ""} onChange={(e) => setH({ ...h, overnight_min_package_id: e.target.value })}>
            <option value="">Otomatis (paket termurah ≥ minimum)</option>
            {(packages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
        <label className="block"><span className="text-[11px] uppercase tracking-widest text-ink-soft">Komisi partner (%)</span>
          <Input type="number" className="mt-1 h-9" data-testid={`hub-commission-${branch.code}`}
            value={h.partner_commission_percent ?? 0}
            onChange={(e) => setH({ ...h, partner_commission_percent: e.target.value })} /></label>
        <div className="flex flex-col gap-2 justify-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid={`hub-overnight-toggle-${branch.code}`} checked={!!h.overnight_enabled}
              onChange={(e) => setH({ ...h, overnight_enabled: e.target.checked })} /> Terima sewa menginap
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid={`hub-venue-toggle-${branch.code}`} checked={!!h.play_at_venue_enabled}
              onChange={(e) => setH({ ...h, play_at_venue_enabled: e.target.checked })} /> Layani Main di Venue
          </label>
        </div>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink-soft flex items-center gap-1.5">
            <CalendarOff size={12} /> Tanggal tutup
          </div>
          <div className="mt-2 space-y-1">
            {(h.closures || []).map((c) => (
              <div key={c.date} className="flex items-center gap-2 text-sm" data-testid={`hub-closure-${c.date}`}>
                <span className="font-medium">{c.date}</span>
                <span className="text-ink-soft text-xs truncate">{c.note}</span>
                <button className="ml-auto text-ink-soft hover:text-lm-red" data-testid={`hub-closure-remove-${c.date}`}
                  onClick={() => setH({ ...h, closures: h.closures.filter((x) => x.date !== c.date) })}><X size={14} /></button>
              </div>
            ))}
            {(h.closures || []).length === 0 && <p className="text-xs text-ink-soft">Belum ada.</p>}
          </div>
          <div className="mt-2 flex gap-2">
            <Input type="date" className="h-9" data-testid={`hub-closure-date-${branch.code}`} value={newClosure.date}
              onChange={(e) => setNewClosure({ ...newClosure, date: e.target.value })} />
            <Input className="h-9" placeholder="Catatan" value={newClosure.note}
              onChange={(e) => setNewClosure({ ...newClosure, note: e.target.value })} />
            <button data-testid={`hub-closure-add-${branch.code}`} className="rounded-md border border-black/15 px-3 text-sm"
              onClick={() => {
                if (!newClosure.date) return;
                setH({ ...h, closures: [...(h.closures || []).filter((x) => x.date !== newClosure.date), newClosure] });
                setNewClosure({ date: "", note: "" });
              }}><Plus size={14} /></button>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-widest text-ink-soft">Jam khusus (tanggal tertentu)</div>
          <div className="mt-2 space-y-1">
            {(h.special_hours || []).map((c) => (
              <div key={c.date} className="flex items-center gap-2 text-sm" data-testid={`hub-special-${c.date}`}>
                <span className="font-medium">{c.date}</span>
                <span className="text-ink-soft text-xs">{c.closed ? "Tutup" : `${c.open}–${c.close}`}</span>
                <button className="ml-auto text-ink-soft hover:text-lm-red" data-testid={`hub-special-remove-${c.date}`}
                  onClick={() => setH({ ...h, special_hours: h.special_hours.filter((x) => x.date !== c.date) })}><X size={14} /></button>
              </div>
            ))}
            {(h.special_hours || []).length === 0 && <p className="text-xs text-ink-soft">Belum ada.</p>}
          </div>
          <div className="mt-2 flex gap-2">
            <Input type="date" className="h-9" data-testid={`hub-special-date-${branch.code}`} value={newSpecial.date}
              onChange={(e) => setNewSpecial({ ...newSpecial, date: e.target.value })} />
            <Input type="time" className="h-9 w-24" data-testid={`hub-special-open-${branch.code}`} value={newSpecial.open}
              onChange={(e) => setNewSpecial({ ...newSpecial, open: e.target.value })} />
            <Input type="time" className="h-9 w-24" data-testid={`hub-special-close-${branch.code}`} value={newSpecial.close}
              onChange={(e) => setNewSpecial({ ...newSpecial, close: e.target.value })} />
            <button data-testid={`hub-special-add-${branch.code}`} className="rounded-md border border-black/15 px-3 text-sm"
              onClick={() => {
                if (!newSpecial.date) return;
                setH({ ...h, special_hours: [...(h.special_hours || []).filter((x) => x.date !== newSpecial.date), { ...newSpecial, closed: false }] });
                setNewSpecial({ date: "", open: "12:00", close: "20:00", note: "" });
              }}><Plus size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};
